import React, { useEffect, useState, lazy, Suspense, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import 'maplibre-gl/dist/maplibre-gl.css';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  MapPin, X, Layers, Loader2, Shield, TreePine, Plus, Trash2, Save
} from 'lucide-react';

const Map3D = lazy(() => import('../components/Map3D'));
const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

const ZONE_COLORS = {
  biosphere: '#10B981',
  depining: '#F97316',
  conservation: '#22C55E',
  education: '#3B82F6',
  hospitality: '#F59E0B',
  public: '#8B5CF6',
  custom: '#6366F1',
};

export default function MapPage() {
  const { language } = useLanguage();
  const [zones, setZones] = useState({ custom_zones: [], marketplace_zones: [] });
  const [selectedZone, setSelectedZone] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminKey, setAdminKey] = useState('');

  // Zone creation form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [formData, setFormData] = useState({
    name: '', zone_type: 'biosphere', description: '', hectares: '', color: '#10B981'
  });

  useEffect(() => { fetchZones(); }, []);

  const fetchZones = async () => {
    try {
      const res = await fetch(`${API}/map/zones`);
      if (res.ok) setZones(await res.json());
    } catch (e) { console.error('Failed to fetch zones:', e); }
  };

  const handleAdminLogin = () => {
    if (adminKey === 'epuyen2026' || adminKey === 'admin') {
      setIsAdmin(true);
      setShowAdminLogin(false);
      setAdminKey('');
      toast.success(language === 'es' ? 'Modo admin activado' : 'Admin mode activated');
    } else {
      toast.error(language === 'es' ? 'Clave incorrecta' : 'Incorrect key');
    }
  };

  const handleCreateZone = useCallback((drawData) => {
    setPendingPolygon(drawData.polygon);
    setShowCreateForm(true);
  }, []);

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'zone_type') next.color = ZONE_COLORS[value] || '#6366F1';
      return next;
    });
  };

  const saveZone = async () => {
    if (!formData.name.trim()) { toast.error('Name required'); return; }
    try {
      const res = await fetch(`${API}/map/zones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          zone_type: formData.zone_type,
          polygon: pendingPolygon,
          color: formData.color,
          description: formData.description,
          hectares: formData.hectares ? parseFloat(formData.hectares) : null,
          status: 'planned'
        })
      });
      if (res.ok) {
        toast.success(language === 'es' ? 'Zona guardada' : 'Zone saved');
        setShowCreateForm(false);
        setPendingPolygon(null);
        setFormData({ name: '', zone_type: 'biosphere', description: '', hectares: '', color: '#10B981' });
        fetchZones();
      } else { toast.error('Failed to create zone'); }
    } catch { toast.error('Network error'); }
  };

  const deleteZone = async (zone) => {
    const zoneId = zone.zone_id;
    const source = zone.source;
    try {
      let url;
      let opts = { method: 'DELETE' };
      if (source === 'marketplace') {
        url = `${API}/marketplace/zones/${zoneId}?force=true`;
      } else {
        url = `${API}/map/zones/${zoneId}`;
      }
      const res = await fetch(url, opts);
      if (res.ok) { toast.success('Zone deleted'); setSelectedZone(null); fetchZones(); }
      else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete zone');
      }
    } catch { toast.error('Failed to delete'); }
  };

  const allZones = [...(zones.custom_zones || []), ...(zones.marketplace_zones || [])];

  return (
    <div className="h-screen flex flex-col" data-testid="map-page">
      <Navbar />
      <div className="flex-1 relative" style={{ marginTop: '64px' }}>
        <div className="absolute inset-0" data-testid="map-3d-container">
          <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-muted"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
            <Map3D zones={zones} onSelectZone={setSelectedZone} isAdmin={isAdmin} onCreateZone={handleCreateZone} />
          </Suspense>
        </div>

        {/* Zone Count */}
        <div className="absolute z-10" style={{ top: '80px', right: '16px' }}>
          <Badge variant="secondary" className="glass-card px-4 py-2 text-sm">
            <Layers className="w-4 h-4 mr-2" />{allZones.length} {language === 'es' ? 'zonas' : 'zones'}
          </Badge>
        </div>

        {/* Admin Toggle */}
        {!isAdmin && (
          <div className="absolute bottom-4 right-4 z-10">
            {showAdminLogin ? (
              <Card className="glass-card w-64"><CardContent className="p-3">
                <div className="flex gap-2">
                  <Input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                    placeholder={language === 'es' ? 'Clave admin' : 'Admin key'}
                    className="text-xs" data-testid="admin-key-input" />
                  <Button size="sm" onClick={handleAdminLogin} data-testid="admin-login-btn"><Shield className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowAdminLogin(false)}><X className="w-3 h-3" /></Button>
                </div>
              </CardContent></Card>
            ) : (
              <button onClick={() => setShowAdminLogin(true)} data-testid="admin-toggle"
                className="glass-card px-3 py-2 rounded-lg text-xs hover:bg-muted transition-colors flex items-center gap-1.5 opacity-60 hover:opacity-100">
                <Shield className="w-3 h-3" /> Admin
              </button>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="absolute bottom-4 right-4 z-10">
            <button onClick={() => { setIsAdmin(false); toast.info('Admin mode off'); }} data-testid="admin-logout"
              className="glass-card px-3 py-2 rounded-lg text-xs hover:bg-muted transition-colors flex items-center gap-1.5 text-emerald-400">
              <Shield className="w-3 h-3" /> Admin ON — click to exit
            </button>
          </div>
        )}

        {/* Create Zone Form Modal */}
        {showCreateForm && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Card className="w-[420px] shadow-2xl border-emerald-500/20" data-testid="create-zone-form">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Plus className="w-5 h-5 text-emerald-500" />
                    {language === 'es' ? 'Nueva Zona Dibujada' : 'New Drawn Zone'}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => { setShowCreateForm(false); setPendingPolygon(null); }}><X className="w-4 h-4" /></Button>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{language === 'es' ? 'Nombre de la zona' : 'Zone name'} *</label>
                  <Input value={formData.name} onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder={language === 'es' ? 'Ej: Biosfera 33ha Norte' : 'E.g. Biosphere 33ha North'}
                    data-testid="zone-name-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{language === 'es' ? 'Tipo' : 'Type'}</label>
                    <select value={formData.zone_type} onChange={(e) => handleFormChange('zone_type', e.target.value)}
                      className="w-full py-2 px-3 bg-background border border-border rounded-md text-sm" data-testid="zone-type-input">
                      <option value="biosphere">Biosphere</option>
                      <option value="depining">De-Pining</option>
                      <option value="conservation">Conservation</option>
                      <option value="education">Education</option>
                      <option value="hospitality">Hospitality</option>
                      <option value="public">Public / Social</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{language === 'es' ? 'Hectáreas' : 'Hectares'}</label>
                    <Input type="number" value={formData.hectares} onChange={(e) => handleFormChange('hectares', e.target.value)}
                      placeholder="33" data-testid="zone-hectares-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Color</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={formData.color} onChange={(e) => handleFormChange('color', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-border" style={{ padding: 0 }} />
                    <div className="flex gap-1.5 flex-wrap flex-1">
                      {Object.entries(ZONE_COLORS).map(([type, col]) => (
                        <button key={type} onClick={() => handleFormChange('color', col)} title={type}
                          className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === col ? 'border-white scale-110 shadow-lg' : 'border-transparent hover:border-white/50'}`}
                          style={{ backgroundColor: col }} />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{language === 'es' ? 'Descripción' : 'Description'}</label>
                  <Textarea value={formData.description} onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder={language === 'es' ? 'Descripción de la zona...' : 'Zone description...'} rows={2} data-testid="zone-desc-input" />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={saveZone} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-zone-btn">
                    <Save className="w-4 h-4 mr-2" /> {language === 'es' ? 'Guardar Zona' : 'Save Zone'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowCreateForm(false); setPendingPolygon(null); }}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Selected Zone Panel */}
        {selectedZone && !showCreateForm && (
          <Card className="absolute bottom-6 left-4 right-4 md:left-auto md:right-4 md:w-96 z-10 glass-card" data-testid="zone-detail-panel">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{selectedZone.name}</h3>
                  <Badge variant="secondary" className="mt-1 text-xs" style={{ backgroundColor: selectedZone.color + '22', color: selectedZone.color }}>
                    {selectedZone.zone_type}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedZone(null)}><X className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-2 text-sm">
                {selectedZone.status && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /><span>Status: {selectedZone.status}</span></div>}
                {selectedZone.hectares && <div className="flex items-center gap-2"><TreePine className="w-4 h-4 text-muted-foreground" /><span>{selectedZone.hectares} ha</span></div>}
                {selectedZone.description && <p className="text-muted-foreground text-xs mt-2">{selectedZone.description}</p>}
                {selectedZone.bounty && <p className="text-xs">Bounty: ${selectedZone.bounty.toLocaleString()}</p>}
              </div>
              {isAdmin && (
                <div className="mt-4 pt-3 border-t border-border">
                  <Button variant="destructive" size="sm" onClick={() => deleteZone(selectedZone)} data-testid="delete-zone-btn">
                    <Trash2 className="w-3 h-3 mr-1" /> {language === 'es' ? 'Eliminar' : 'Delete'}
                  </Button>
                  {selectedZone.source === 'marketplace' && (
                    <p className="text-xs text-muted-foreground mt-1">This will also remove related claims</p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
